import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Document2026, DocumentSection } from '../models/document2026.model';

@Injectable({
  providedIn: 'root'
})
export class Document2026Service {
  private readonly START_DATE = new Date('2025-11-24T00:00:00');
  private readonly END_DATE = new Date('2027-12-31T23:59:59');
  private readonly EXPIRY_MESSAGE_START_DATE = new Date('2027-12-01T00:00:00');
  private readonly VALID_GUID = 'b58596ce-e134-4aa2-81c9-70850d6e9d13';

  constructor(private http: HttpClient) { }

  isValidGuid(guid: string): boolean {
    return guid === this.VALID_GUID;
  }

  isAvailable(): boolean {
    const now = new Date();
    return now >= this.START_DATE && now <= this.END_DATE;
  }

  shouldShowExpiryMessage(): boolean {
    const now = new Date();
    return now >= this.EXPIRY_MESSAGE_START_DATE && now > this.END_DATE;
  }

  getRemainingDays(): number {
    if (!this.isAvailable()) {
      return 0;
    }
    const now = new Date();
    const diffTime = this.END_DATE.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getDocuments(): Observable<DocumentSection[]> {
    const documents: Document2026[] = [
      {
        id: 'muster-1',
        title: 'Muster-Dokument',
        filename: 'muster-dokument.pdf',
        category: 'muster',
        assetPath: 'assets/limes/muster-dokument.pdf'
      }
    ];

    const sections: DocumentSection[] = [
      {
        id: 'muster',
        title: 'Muster',
        documents
      }
    ];

    return of(sections.filter(section => section.documents.length > 0));
  }

  loadPdfAsBlob(assetPath: string): Observable<Blob> {
    const normalizedPath = assetPath.startsWith('/') ? assetPath : '/' + assetPath;

    const pathParts = normalizedPath.split('/');
    const processedParts = pathParts.map((part, index) => {
      if (!part) {
        return part;
      }
      if (part === 'assets' || part === '2026' || part === 'limes') {
        return part;
      }
      if (index === pathParts.length - 1 && part.includes(' ')) {
        return encodeURIComponent(part);
      }
      return part;
    });

    const finalPath = processedParts.join('/');

    return this.http.get(finalPath, {
      responseType: 'blob',
      observe: 'body'
    });
  }

  loadAllPdfsAsBlobs(documents: Document2026[]): Observable<Array<{ document: Document2026; blob: Blob }>> {
    const requests = documents.map(doc =>
      this.loadPdfAsBlob(doc.assetPath).pipe(
        map(blob => ({ document: doc, blob }))
      )
    );
    return new Observable(observer => {
      const results: Array<{ document: Document2026; blob: Blob }> = [];
      let completed = 0;

      requests.forEach((request, index) => {
        request.subscribe({
          next: (result) => {
            results[index] = result;
            completed++;
            if (completed === requests.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            console.error(`Fehler beim Laden von ${documents[index].filename}:`, error);
            completed++;
            if (completed === requests.length) {
              observer.next(results);
              observer.complete();
            }
          }
        });
      });
    });
  }
}
